import React, { useEffect, useState } from 'react';
import { AdminSidebar } from './AdminSidebar';
import { useAdmin } from '../context/AdminContext';
import { Navigate, Link } from 'react-router-dom';
import { Menu, User, Bell, X, Info, CheckCircle, AlertCircle } from 'lucide-react';
import { auditLog } from '../services/analyticsService';

export const AdminLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, stats } = useAdmin();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [adminNotifications, setAdminNotifications] = useState<any[]>([]);

  if (!user) return <Navigate to="/admin/login" replace />;

  useEffect(() => {
    const loadNotifications = async () => {
      const logs = await auditLog.getLogs(6, 0);
      const items = logs.map((log, index) => ({
        id: log.id || `log-${index}`,
        text: `${log.actor_name || 'System'} ${log.action.toLowerCase().replace('_', ' ')}`,
        type: 'info',
        time: new Date(log.created_at).toLocaleTimeString()
      }));

      if (stats.pendingReviews > 0) {
        items.unshift({
          id: 'review-queue',
          text: `Review Queue: ${stats.pendingReviews} items pending`,
          type: 'alert',
          time: 'Just now'
        });
      }

      setAdminNotifications(items.slice(0, 6));
    };

    loadNotifications();
  }, [stats.pendingReviews]);

  return (
    <div className="flex bg-gray-50 min-h-screen">
      {/* Sidebar manages its own persistence from 'md' up */}
      <AdminSidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile Header - Hidden from 'md' up */}
        <header className="md:hidden bg-white border-b border-gray-200 h-16 flex items-center justify-between px-4 sticky top-0 z-40">
           <button onClick={() => setIsSidebarOpen(true)} className="p-2 text-gray-500 hover:text-brand-900">
             <Menu size={24} />
           </button>
           <Link to="/admin/dashboard" className="flex items-baseline gap-1">
               <span className="text-xl font-serif font-black tracking-tighter text-brand-900 uppercase">Nida</span>
               <span className="text-[10px] font-bold tracking-widest text-brand-500 uppercase">Staff</span>
           </Link>
           <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
             <User size={18} />
           </div>
        </header>

        {/* Desktop & Tablet Top Bar */}
        <header className="hidden md:flex bg-white border-b border-gray-100 h-16 items-center justify-end px-8 sticky top-0 z-20">
            <div className="flex items-center gap-6 relative">
                {/* Fixed & Functional Notification Icon */}
                <button 
                  onClick={() => setShowNotifications(!showNotifications)}
                  className={`relative p-2 rounded-lg transition-all duration-200 ${
                    showNotifications ? 'bg-brand-50 text-brand-900' : 'text-gray-400 hover:text-brand-900 hover:bg-gray-50'
                  }`}
                >
                  <Bell size={20} />
                  {stats.pendingReviews > 0 && (
                    <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-brand-500 rounded-full ring-2 ring-white"></span>
                  )}
                </button>

                {/* Admin Notification Dropdown */}
                {showNotifications && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowNotifications(false)} />
                    <div className="absolute top-12 right-0 w-80 bg-white border border-gray-200 shadow-2xl rounded-2xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2">
                      <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                        <h4 className="text-[10px] font-black uppercase tracking-widest text-brand-900">Admin Alerts</h4>
                        <button onClick={() => setShowNotifications(false)} className="text-gray-400 hover:text-brand-900">
                          <X size={14} />
                        </button>
                      </div>
                      <div className="max-h-[400px] overflow-y-auto no-scrollbar">
                        {adminNotifications.map((notif) => (
                          <div key={notif.id} className="p-4 border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors cursor-pointer group">
                            <div className="flex gap-3">
                              <div className={`mt-0.5 ${
                                notif.type === 'alert' ? 'text-brand-500' : 
                                notif.type === 'success' ? 'text-green-500' : 'text-blue-500'
                              }`}>
                                {notif.type === 'alert' ? <AlertCircle size={16}/> : 
                                 notif.type === 'success' ? <CheckCircle size={16}/> : <Info size={16}/>}
                              </div>
                              <div>
                                <p className="text-sm font-bold text-brand-900 leading-tight group-hover:text-brand-500 transition-colors">{notif.text}</p>
                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1">{notif.time}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <Link 
                        to="/admin/logs" 
                        onClick={() => setShowNotifications(false)}
                        className="block w-full py-3 text-center text-[10px] font-black uppercase tracking-widest text-brand-500 hover:bg-brand-900 hover:text-white transition-all border-t border-gray-100"
                      >
                        View System Logs
                      </Link>
                    </div>
                  </>
                )}
            </div>
        </header>

        <main className="flex-1 max-w-7xl w-full mx-auto p-4 md:p-8 lg:p-12">
          {children}
        </main>
      </div>
    </div>
  );
};